import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import apiService from '../../services/api';

interface SourceType {
  id?: number;
  part_code: string;
  part_type: string;
  specimen_part_id?: number;
  collection_method: string;
  collectionDatetime: string;
  site: string;
  condition: string;
  submitted_by: string;
  cpt_code: string;
}

interface User {
  id: number;
  name: string;
  role: string;
}

interface SpecimenDialogProps {
  open: boolean;
  onClose: () => void;
  editingSpecimenIdx: number | null;
  specimenForm: SourceType;
  onSave: (specimen: SourceType) => Promise<void>;
  disabled?: boolean;
}

const specimenConditionOptions = [
  "Received in formalin",
  "Unfixed",
  "Partially fixed",
  "Clotted",
  "Hemolyzed",
  "Leaking container",
  "Desiccated",
  "Label discrepancy",
  "Frozen",
  "Adequate",
  "Unsatisfactory",
  "Broken slide"
];

// Helper to get current datetime in user's timezone in 'YYYY-MM-DDTHH:mm' format
function getLocalDateTimeString() {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

// Helper to convert ISO datetime string to datetime-local format
function convertToDateTimeLocal(isoString: string | null | undefined): string {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '';
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60 * 1000);
    return local.toISOString().slice(0, 16);
  } catch {
    return '';
  }
}

// Helper to convert datetime-local format back to ISO format
function convertFromDateTimeLocal(dateTimeLocal: string | null | undefined): string {
  if (!dateTimeLocal || dateTimeLocal.trim() === '') return new Date().toISOString();
  try {
    // Ensure the datetime string has seconds if missing
    let normalizedDateTime = dateTimeLocal.trim();
    if (normalizedDateTime.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) {
      // Add seconds if missing (format: YYYY-MM-DDTHH:mm)
      normalizedDateTime += ':00';
    }
    
    // Add milliseconds if missing
    if (!normalizedDateTime.includes('.') && normalizedDateTime.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/)) {
      normalizedDateTime += '.000';
    }
    
    // Create a Date object from the datetime-local string (which is in local timezone)
    const date = new Date(normalizedDateTime);
    if (isNaN(date.getTime())) return new Date().toISOString();
    // Return as ISO string (with timezone info)
    return date.toISOString();
  } catch {
    return new Date().toISOString();
  }
}

// Helper to get next available part code letter
function getNextAvailablePartCode(usedCodes: string[]): string {
  let nextCharCode = 65; // 'A'
  while (usedCodes.map(c => c.toUpperCase()).includes(String.fromCharCode(nextCharCode))) {
    nextCharCode++;
  }
  return String.fromCharCode(nextCharCode);
}

const SpecimenDialog: React.FC<SpecimenDialogProps> = ({
  open,
  onClose,
  editingSpecimenIdx,
  specimenForm,
  onSave,
  disabled = false,
}) => {
  const [form, setForm] = useState<SourceType>(specimenForm);
  const [availableParts, setAvailableParts] = useState<any[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [cptInputValue, setCptInputValue] = useState('');
  const [cptOptions, setCptOptions] = useState<{ id: string; code: string; description: string; category?: string }[]>([]);
  const [cptLoading, setCptLoading] = useState(false);
  const [cptPage, setCptPage] = useState(1);
  const [cptTotal, setCptTotal] = useState(0);
  const cptListRef = useRef<HTMLUListElement | null>(null);

  // Helper function to get collection methods for a part
  const getCollectionMethods = (partName: string) => {
    console.log('Getting collection methods for part:', partName);
    console.log('Available parts:', availableParts);
    
    const part = availableParts.find(ap => ap.partName === partName);
    console.log('Found part:', part);
    
    if (!part?.collectionMethod) {
      console.log('No collection method found for part');
      return [];
    }
    
    // Debug logging
    console.log('Raw collectionMethod data:', part.collectionMethod);
    console.log('Type of collectionMethod:', typeof part.collectionMethod);
    
    try {
      let methods;
      
      if (typeof part.collectionMethod === 'string') {
        // Clean the string first - handle common JSON issues
        let cleanedJson = part.collectionMethod.trim();
        
        // Handle malformed JSON that might be missing quotes around property names
        if (!cleanedJson.startsWith('[') && !cleanedJson.startsWith('{')) {
          // If it's just a simple value, wrap it in an array
          console.log('Simple value detected, wrapping in array');
          return [cleanedJson];
        }
        
        // Try to fix common JSON issues
        if (cleanedJson.includes('=') || (!cleanedJson.includes(':') && !cleanedJson.startsWith('['))) {
          console.log('Detected non-JSON format, attempting to parse as simple list');
          // Handle formats like "method1,method2" or "method1=value,method2=value"
          const simpleList = cleanedJson.split(',').map((item: string) => {
            return item.split('=')[0].trim(); // Take part before = if exists
          }).filter((item: string) => item.length > 0);
          return simpleList;
        }
        
        console.log('Attempting JSON parse of:', cleanedJson);
        methods = JSON.parse(cleanedJson);
      } else {
        methods = part.collectionMethod;
      }
      
      console.log('Parsed collection methods:', methods);
      
      // Ensure it's an array and clean up the values
      let result = Array.isArray(methods) ? methods : 
                   methods && typeof methods === 'object' ? Object.keys(methods) :
                   [methods].filter(Boolean);
      
      // Clean up any remaining curly braces or JSON artifacts
      result = result.map((method: any) => {
        if (typeof method === 'string') {
          return method.replace(/[{}]/g, '').trim();
        }
        return String(method).replace(/[{}]/g, '').trim();
      }).filter((method: string) => method.length > 0);
      
      console.log('Final collection methods:', result);
      return result;
    } catch (error) {
      console.error('Error parsing collection methods for part:', partName);
      console.error('Raw data was:', part.collectionMethod);
      console.error('Error:', error);
      
      // Fallback: try to extract something useful from the raw string
      if (typeof part.collectionMethod === 'string') {
        const fallbackMethods = part.collectionMethod
          .split(/[,;|\n]/) // Split on common delimiters
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0 && !item.includes('{') && !item.includes('}'));
        
        console.log('Fallback collection methods:', fallbackMethods);
        return fallbackMethods;
      }
      
      return [];
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    console.log("SpecimenDialog - Original form data:", form);
    console.log("SpecimenDialog - form.collectionDatetime:", form.collectionDatetime);
    
    const convertedDatetime = convertFromDateTimeLocal(form.collectionDatetime);
    console.log("SpecimenDialog - Converted datetime:", convertedDatetime);
    
    const specimenPayload = {
      ...form,
      collectionDatetime: convertedDatetime
    };
    console.log("SpecimenDialog - Final payload:", specimenPayload);
    
    await onSave(specimenPayload);
    onClose();
  };

  // Fetch paginated CPT codes
  const fetchCptCodes = async (input: string, page: number = 1, append = false) => {
    setCptLoading(true);
    try {
      const data = await apiService.getCptCodes(page, 20, input.trim() || undefined);
      setCptOptions(prev => append ? [...prev, ...(data.codes || [])] : (data.codes || []));
      setCptTotal(data.total || 0);
      setCptPage(page);
    } catch (error) {
      console.error('Error fetching CPT codes:', error);
      setCptOptions([]);
      setCptTotal(0);
    } finally {
      setCptLoading(false);
    }
  };

  // Handle scroll for pagination
  const handleCptScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const listbox = event.currentTarget;
    if (cptLoading) return;
    if (listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 10) {
      if (cptOptions.length < cptTotal) {
        fetchCptCodes(cptInputValue, cptPage + 1, true);
      }
    }
  };

  // Fetch users and specimen parts when dialog opens
  useEffect(() => {
    if (open) {
      // Fetch users
      apiService.getUsers(1, 1000)
        .then(data => {
          const usersData = Array.isArray(data.users) ? data.users : Array.isArray(data) ? data : [];
          setUsers(usersData);
        })
        .catch(() => setUsers([]));
      
      // Fetch specimen parts
      apiService.getSpecimenParts()
        .then(data => {
          const partsData = Array.isArray(data) ? data : [];
          console.log('Specimen parts loaded:', partsData);
          setAvailableParts(partsData);
        })
        .catch((error) => {
          console.error('Error fetching specimen parts:', error);
          setAvailableParts([]);
        });
    }
  }, [open]);

  // Fetch CPT codes on dialog open
  useEffect(() => {
    if (open) {
      setCptOptions([]);
      setCptPage(1);
      fetchCptCodes('', 1, false);
    }
  }, [open]);

  // Debounced search for CPT codes
  useEffect(() => {
    if (!open) return;
    
    const timeoutId = setTimeout(() => {
      setCptOptions([]);
      setCptPage(1);
      fetchCptCodes(cptInputValue, 1, false);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [cptInputValue, open]);

  // When partType changes, set default CPT code for that part if available
  useEffect(() => {
    if (!form.part_type) return;
    
    // Find the available part
    const part = availableParts.find(ap => ap.partName === form.part_type);
    if (!part) return;
    
    // Find CPT codes for this part from specimen mapping
    apiService.getCptCodesByPart(part.id)
      .then(response => {
        console.log('CPT codes by part response:', response);
        
        // Handle different response structures
        const mappings = response?.mappings || response?.data || response || [];
        
        if (Array.isArray(mappings) && mappings.length > 0) {
          // Get all CPT codes for this part
          const cptCodes = mappings
            .filter((mapping: any) => mapping.codeType === 'CPT' && mapping.active)
            .map((mapping: any) => ({
              id: mapping.id || mapping.code,
              code: mapping.code,
              description: mapping.description || 'No description',
              category: mapping.category || 'Specimen Mapping'
            }));
          
          if (cptCodes.length > 0) {
            // Set the first CPT code as default
            setForm(prev => ({ ...prev, cpt_code: cptCodes[0].code }));
            
            // Update CPT options to include the mapped codes at the top
            setCptOptions(prev => {
              // Remove duplicates and put mapped codes first
              const existingCodes = prev.map(opt => opt.code);
              const newCodes = cptCodes.filter(cpt => !existingCodes.includes(cpt.code));
              return [...newCodes, ...prev];
            });
            
            console.log(`Set default CPT code: ${cptCodes[0].code} for specimen part: ${form.part_type}`);
          }
        } else {
          // No mappings found, clear the CPT code
          console.log(`No CPT mappings found for specimen part: ${form.part_type}`);
          setForm(prev => ({ ...prev, cpt_code: '' }));
        }
      })
      .catch(error => {
        console.error('Error fetching CPT codes for part:', error);
      });
  }, [form.part_type, availableParts]);

  // Update form when specimenForm prop changes
  useEffect(() => {
    setForm({
      ...specimenForm,
      collectionDatetime: convertToDateTimeLocal(specimenForm.collectionDatetime)
    });
  }, [specimenForm]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {editingSpecimenIdx !== null ? 'Edit Specimen' : 'Add Specimen'}
      </DialogTitle>
      <DialogContent>
        <TextField
          margin="dense"
          label="Part Code"
          name="part_code"
          value={form.part_code}
          onChange={handleFormChange}
          fullWidth
          required
          disabled={disabled}
        />
        <TextField
          select
          margin="dense"
          label="Specimen Part Type"
          name="part_type"
          value={form.part_type}
          onChange={e => {
            const partType = e.target.value;
            const selectedPart = availableParts.find(ap => ap.partName === partType);
            setForm(prev => ({
              ...prev,
              part_type: partType,
              specimen_part_id: selectedPart ? selectedPart.id : undefined
            }));
          }}
          fullWidth
          required
          disabled={disabled}
        >
          <MenuItem value="">Select Part Type</MenuItem>
          {Array.isArray(availableParts) ? (availableParts || []).map(ap => (
            <MenuItem key={ap.id} value={ap.partName}>{ap.partName}</MenuItem>
          )) : []}
        </TextField>
        <TextField
          select
          margin="dense"
          label="Collection Method"
          name="collection_method"
          value={form.collection_method}
          onChange={handleFormChange}
          fullWidth
          disabled={disabled || !form.part_type}
        >
          <MenuItem value="">{!form.part_type ? 'Select Specimen Part First' : 'Select Method'}</MenuItem>
          {getCollectionMethods(form.part_type).map((method: string, idx: number) => (
            <MenuItem key={idx} value={method}>{method}</MenuItem>
          ))}
        </TextField>
        <TextField
          margin="dense"
          label="Collection Date/Time"
          name="collectionDatetime"
          type="datetime-local"
          value={form.collectionDatetime}
          onChange={handleFormChange}
          fullWidth
          required
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
        />
        <TextField
          margin="dense"
          label="Specimen Site"
          name="site"
          value={form.site}
          onChange={handleFormChange}
          fullWidth
          disabled={disabled}
        />
        <Autocomplete
          options={specimenConditionOptions}
          value={form.condition}
          onChange={(_, value) => setForm(prev => ({ ...prev, condition: value || '' }))}
          renderInput={params => (
            <TextField 
              {...params} 
              label="Specimen Condition" 
              fullWidth 
              margin="dense" 
              disabled={disabled} 
            />
          )}
          isOptionEqualToValue={(option, value) => option === value}
          freeSolo={false}
        />
        <Autocomplete
          options={Array.isArray(users) ? users : []}
          getOptionLabel={option => option.name}
          value={users.find(u => u.name === form.submitted_by) || null}
          onChange={(_, value) => setForm(prev => ({ ...prev, submitted_by: value ? value.name : '' }))}
          renderInput={params => (
            <TextField 
              {...params} 
              label="Submitted By" 
              fullWidth 
              margin="dense" 
              disabled={disabled} 
            />
          )}
          isOptionEqualToValue={(option, value) => option.name === value.name}
          clearOnEscape
        />
        <Autocomplete
          value={form.cpt_code || null}
          onChange={(_, value) => {
            // Extract just the code part if it's in "code - description" format
            const code = typeof value === 'string' ? value.split(' - ')[0] : value;
            setForm(prev => ({ ...prev, cpt_code: code || '' }));
          }}
          inputValue={cptInputValue}
          onInputChange={(_, value) => setCptInputValue(value)}
          options={(cptOptions || []).map(opt => `${opt.code} - ${opt.description || 'No description'}`)}
          loading={cptLoading}
          renderInput={params => (
            <TextField 
              {...params} 
              label="CPT Code" 
              margin="dense" 
              fullWidth 
              disabled={disabled}
              helperText="Search by code or description"
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <div>
                <strong>{option.split(' - ')[0]}</strong>
                <br />
                <small style={{ color: '#666' }}>{option.split(' - ')[1]}</small>
              </div>
            </li>
          )}
          ListboxProps={{ onScroll: handleCptScroll, ref: cptListRef }}
          openOnFocus
          freeSolo={false}
          filterOptions={(options) => options} // Don't filter on frontend since we're doing server-side search
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={disabled}>
          Save Specimen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpecimenDialog; 